// Unit conversion and state management

export type UnitSystem = 'metric' | 'imperial';

const METERS_TO_FEET = 3.28084;
const STORAGE_KEY = 'borebeast-units';

// Current unit system state
let currentSystem: UnitSystem = loadPreference();

function loadPreference(): UnitSystem {
  if (typeof localStorage !== 'undefined') {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'imperial' || saved === 'metric') {
      return saved;
    }
  }
  return 'imperial'; // Default to feet for US market
}

function savePreference(system: UnitSystem): void {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(STORAGE_KEY, system);
  }
}

// Get/set current unit system
export function getUnitSystem(): UnitSystem {
  return currentSystem;
}

export function setUnitSystem(system: UnitSystem): void {
  currentSystem = system;
  savePreference(system);
}

export function toggleUnits(): UnitSystem {
  const newSystem = currentSystem === 'metric' ? 'imperial' : 'metric';
  setUnitSystem(newSystem);
  return newSystem;
}

export function isMetric(): boolean {
  return currentSystem === 'metric';
}

// Conversion functions (always take meters, return in current unit)
export function convertDistance(meters: number): number {
  return currentSystem === 'metric' ? meters : meters * METERS_TO_FEET;
}

export function convertElevation(meters: number): number {
  return currentSystem === 'metric' ? meters : meters * METERS_TO_FEET;
}

export function convertDepth(meters: number): number {
  return currentSystem === 'metric' ? meters : meters * METERS_TO_FEET;
}

// Convert from display units back to meters (for input fields)
export function toMeters(value: number): number {
  return currentSystem === 'metric' ? value : value / METERS_TO_FEET;
}

// Labels
export function distanceUnit(): string {
  return currentSystem === 'metric' ? 'm' : 'ft';
}

export function distanceUnitLong(): string {
  return currentSystem === 'metric' ? 'metres' : 'feet';
}

export function elevationUnit(): string {
  return currentSystem === 'metric' ? 'm' : 'ft';
}

export function depthUnit(): string {
  return currentSystem === 'metric' ? 'm' : 'ft';
}

// Format functions with units
export function formatDistance(meters: number, decimals: number = 1): string {
  return `${convertDistance(meters).toFixed(decimals)} ${distanceUnit()}`;
}

export function formatElevation(meters: number, decimals: number = 2): string {
  return `${convertElevation(meters).toFixed(decimals)} ${elevationUnit()}`;
}

export function formatDepth(meters: number, decimals: number = 2): string {
  return `${convertDepth(meters).toFixed(decimals)} ${depthUnit()}`;
}

// For dogleg - display in appropriate units
export function doglegUnit(): string {
  return currentSystem === 'metric' ? '°/30m' : '°/100ft';
}

// Marker intervals (in meters internally, but displayed in current units)
export function getMarkerInterval(): number {
  // 100m for metric, ~100ft (30m) for imperial
  return currentSystem === 'metric' ? 100 : 30.48; // 100 feet in meters
}

export function formatMarkerDistance(meters: number): string {
  const value = convertDistance(meters);
  // Round to nice numbers
  const rounded = currentSystem === 'metric'
    ? Math.round(value)
    : Math.round(value / 10) * 10; // Round to nearest 10 feet
  return `${rounded}${distanceUnit()}`;
}
